import React, { useState, useEffect } from 'react';
import { db } from '../utils/firebase';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { useAuth } from '../hooks/useAuth';
import ProfileSettings from './ProfileSettings';

const AVATARS = {
  moon: '🌙',
  star: '🌠',
  cloud: '☁️',
  sparkles: '✨',
  night: '🌌',
  sunset: '🌅',
  rainbow: '🌈',
  comet: '☄️',
  galaxy: '🌌',
  aurora: '🌌',
  crystal: '💎',
  lantern: '🏮'
};

interface UserProfileData {
  displayName: string;
  icon: string;
  description: string;
  createdAt: Date;
}

interface MemoryStats {
  total: number;
  public: number;
}

interface UserProfileProps {
  userId?: string;  // Optional - if not provided, shows current user's profile
  onSettingsClick?: () => void;  // Optional - only shown for current user
}

const UserProfile: React.FC<UserProfileProps> = ({ userId, onSettingsClick }) => {
  const { user } = useAuth();
  const [profile, setProfile] = useState<UserProfileData | null>(null);
  const [loading, setLoading] = useState(true);
  const [memoryStats, setMemoryStats] = useState<MemoryStats>({ total: 0, public: 0 });
  const isCurrentUser = !userId || (user && user.uid === userId);
  const targetUserId = userId || user?.uid;

  useEffect(() => {
    const fetchProfile = async () => {
      if (!targetUserId) {
        setLoading(false);
        return;
      }

      try {
        const userRef = doc(db, 'users', targetUserId);
        const docSnap = await getDoc(userRef);

        if (docSnap.exists()) {
          const userData = docSnap.data();
          setProfile({
            displayName: userData.displayName || 'Dreamer',
            icon: userData.icon || '🌙',
            description: userData.description || 'A mysterious dreamer...',
            createdAt: userData.createdAt?.toDate() || new Date()
          });
        } else {
          setProfile({
            displayName: 'Dreamer',
            icon: '🌙',
            description: 'A mysterious dreamer...',
            createdAt: new Date()
          });
        }
      } catch (error) {
        console.error('Error fetching profile');
        setProfile({
          displayName: 'Dreamer',
          icon: '🌙',
          description: 'A mysterious dreamer...',
          createdAt: new Date()
        });
      } finally {
        setLoading(false);
      }
    };

    const fetchMemoryStats = async () => {
      if (!targetUserId) return;

      try {
        const dreamsRef = collection(db, 'dreams');
        // If viewing another user's profile, only count public dreams
        const constraints = [where('userId', '==', targetUserId)];
        if (!isCurrentUser) {
          constraints.push(where('isPublic', '==', true));
        }
        const q = query(dreamsRef, ...constraints);
        const querySnapshot = await getDocs(q);

        const stats: MemoryStats = {
          total: querySnapshot.size,
          public: isCurrentUser 
            ? querySnapshot.docs.filter(doc => doc.data().isPublic).length 
            : querySnapshot.size
        };

        setMemoryStats(stats);
      } catch (error) {
        console.error('Error fetching dream stats');
      }
    };

    fetchProfile();
    fetchMemoryStats();
  }, [targetUserId, isCurrentUser]);

  if (loading || !profile) {
    return (
      <div className="relative p-4 sm:p-8 rounded-2xl overflow-hidden
                    bg-gradient-to-b from-dream-800/90 to-dream-900/90
                    border border-dream-700/30 shadow-2xl backdrop-blur-lg">
        <div className="flex items-center space-x-4 animate-pulse">
          <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-dream-700/30" />
          <div className="flex-1">
            <div className="h-6 w-32 bg-dream-700/30 rounded mb-2" />
            <div className="h-4 w-48 bg-dream-700/30 rounded" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative p-4 sm:p-8 rounded-2xl overflow-hidden
                  bg-gradient-to-b from-dream-800/90 to-dream-900/90
                  border border-dream-700/30 shadow-2xl backdrop-blur-lg">
      <div className="absolute -z-10 top-0 left-0 right-0 bottom-0 bg-gradient-to-br 
                    from-dream-500/5 via-transparent to-mist-500/5
                    rounded-2xl opacity-50"></div>

      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-4 sm:mb-6">
        <div className="flex items-center space-x-4">
          <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-dream-700/50 border-2 border-dream-600/50
                        flex items-center justify-center text-2xl sm:text-4xl backdrop-blur-sm">
            {profile?.icon || '🌙'}
          </div>
          <div>
            <h2 className="text-xl sm:text-2xl font-bold text-dream-50">
              {profile?.displayName || 'Dreamer'}
            </h2>
            <p className="text-sm sm:text-base text-dream-300 italic">
              {profile?.description || 'A mysterious dreamer...'}
            </p>
          </div>
        </div>
        {isCurrentUser && onSettingsClick && (
          <button
            onClick={onSettingsClick}
            className="w-full sm:w-auto px-4 py-2 rounded-lg bg-dream-700/50 hover:bg-dream-700/70
                     text-dream-50 transition-colors
                     border border-dream-600/30 hover:border-dream-500/50
                     backdrop-blur-sm text-sm sm:text-base"
          >
            Edit Profile
          </button>
        )}
      </div>

      <div className="grid grid-cols-2 gap-2 sm:gap-4 text-dream-200">
        <div className="p-3 sm:p-4 rounded-lg bg-dream-800/50 border border-dream-700/30">
          <div className="text-xs sm:text-sm text-dream-300">Dreams</div>
          <div className="flex items-baseline space-x-2">
            <div className="text-lg sm:text-2xl font-bold text-dream-50">{memoryStats.total}</div>
            {isCurrentUser && (
              <div className="text-xs sm:text-sm text-dream-500">({memoryStats.public} shared)</div>
            )}
          </div>
        </div>
        <div className="p-3 sm:p-4 rounded-lg bg-dream-800/50 border border-dream-700/30">
          <div className="text-xs sm:text-sm text-dream-300">Dreaming Since</div>
          <div className="text-lg sm:text-2xl font-bold text-dream-50 truncate">
            {profile?.createdAt ? new Date(profile.createdAt).toLocaleDateString() : 'Today'}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserProfile; 